import { Fragment, useMemo, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { CallId } from 'shared/models';
import { State } from 'reducers';
import { getBuilding } from 'selectors/buildingsSelectors';
import { getVideoCall } from 'selectors/ecsSelectors';
import { Link } from 'react-router-dom';
import { getUser } from 'selectors/usersSelectors';

export type Props = {
    callId?: CallId;
};

export const MSCallEventDetails = (props: Props) => {
    const { t } = useTranslation();

    const videoCall = useSelector((state: State) => getVideoCall(state, props));
    const building = useSelector((state: State) => videoCall && getBuilding(state, videoCall));
    const userId = (videoCall && videoCall.userId) || undefined;
    const user = useSelector((state: State) => videoCall && getUser(state, { userId }));
    const userName = user && (user.displayName || user.emailAddress);

    const elevatorId = videoCall && videoCall.elevatorId;
    const elevatorName = (videoCall && videoCall.elevatorName) || elevatorId;

    const buildingId = videoCall && videoCall.buildingId;
    const buildingUrlSlug = building && building.urlSlug;
    const buildingName = building && building.displayName;

    const source = videoCall && videoCall.source;

    const nodes = useMemo<ReactNode>(() => {
        if (!source) return [];

        const template = t(source === 'MS' ? 'VIDEO.EVENT.MSCL_NEW_DETAIL' : 'VIDEO.EVENT.MSCL_DETAIL');
        
        const parts: ReactNode[] = [];
        template.replace(/([^<]*)(?:<([^>]+)\/>)?/g, (match, text: string, tag: string) => {
            if (text) {
                parts.push(
                    <span key={ parts.length.toString() }>
                        { text }
                    </span>
                );
            }
            if (tag === 'ELEVATOR') {
                parts.push(
                    <Link to={ `/elevator/${elevatorId}` } key={ parts.length.toString() }>
                        { elevatorName || '?' }
                    </Link>
                );
            }
            else if (tag === 'BUILDING') {
                parts.push(
                    buildingUrlSlug
                        ? <Link to={ `/buildings/${buildingUrlSlug || buildingId}` } key={ parts.length.toString() }>
                            { buildingName }
                        </Link>
                        : <b key={ parts.length.toString() }>
                            { buildingId }
                        </b>
                );
            }
            else if (tag === 'USER' && userName) {
                parts.push(
                    <b key={ parts.length.toString() }>
                        { userName }
                    </b>
                );
            }
            return '';
        });
        return parts;
    }, [ elevatorName, elevatorId, buildingName, buildingUrlSlug, buildingId, userName, source, t ]);

    return (
        <Fragment>
            { nodes }
        </Fragment>
    );
};
